import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ThumbsUp, ThumbsDown, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VoteBox = ({ language = 'de' }) => {
  const [stats, setStats] = useState({ likes: 0, dislikes: 0, views: 0, user_vote: null });
  const [loading, setLoading] = useState(false);
  const viewTracked = useRef(false);

  useEffect(() => {
    fetchVotes();
    // Only track view once per component mount
    if (!viewTracked.current) {
      viewTracked.current = true;
      trackView();
    }
  }, []);

  const fetchVotes = async () => {
    try {
      const res = await axios.get(`${API}/votes`);
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch votes:', error);
    }
  };

  const trackView = async () => {
    try {
      const res = await axios.post(`${API}/views`);
      // Only update views, don't touch other stats
      setStats(prev => ({ ...prev, views: res.data.views }));
    } catch (error) {
      console.error('Failed to track view:', error);
    }
  };

  const handleVote = async (voteType) => {
    if (loading) return;
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/votes`, { vote_type: voteType });
      // IMPORTANT: Only update likes, dislikes, and user_vote - NOT views!
      // The vote endpoint doesn't return the correct view count
      setStats(prev => ({
        ...prev,
        likes: res.data.likes,
        dislikes: res.data.dislikes,
        user_vote: res.data.user_vote
        // views stays unchanged from prev
      }));
    } catch (error) {
      console.error('Failed to vote:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid="vote-box">
      {/* View Counter */}
      <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded border border-cyan-electric/20">
        <Eye className="w-4 h-4 text-cyan-electric" />
        <span className="font-mono text-sm text-slate-400">{stats.views}</span>
      </div>
      
      <span className="font-rajdhani text-xs uppercase tracking-wider text-slate-400 hidden sm:inline">
        {language === 'de' ? 'Gefällt dir?' : 'Like it?'}
      </span>
      
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleVote('like')}
          disabled={loading}
          className={`flex items-center gap-2 px-3 py-2 rounded transition-all ${
            stats.user_vote === 'like'
              ? 'bg-green-500/20 text-green-400 border border-green-500/50'
              : 'bg-white/5 text-slate-400 hover:text-green-400 hover:bg-green-500/10 border border-transparent'
          }`}
          data-testid="vote-like"
        >
          <ThumbsUp className="w-4 h-4" />
          <span className="font-mono text-sm">{stats.likes}</span>
        </button>
        
        <button
          onClick={() => handleVote('dislike')}
          disabled={loading}
          className={`flex items-center gap-2 px-3 py-2 rounded transition-all ${
            stats.user_vote === 'dislike'
              ? 'bg-red-500/20 text-red-400 border border-red-500/50'
              : 'bg-white/5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 border border-transparent'
          }`}
          data-testid="vote-dislike"
        >
          <ThumbsDown className="w-4 h-4" />
          <span className="font-mono text-sm">{stats.dislikes}</span>
        </button>
      </div>
    </div>
  );
};

export default VoteBox;
